import { useState } from "react";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { useReveal } from "../lib/reveal";

const faqs = [
  {
    q: "What is the typical import/export turnaround?",
    a: "Standard ocean freight ranges from 18–35 days depending on the route, while air cargo can deliver in 3–7 days. We share an accurate ETA with every quote.",
  },
  {
    q: "What payment terms do you offer?",
    a: "We support advance, partial advance with balance against documents, and LC-based payments. Terms are finalized after KYC and order confirmation.",
  },
  {
    q: "Do you handle customs clearance and documentation?",
    a: "Yes. We manage end-to-end documentation, customs clearance and regulatory compliance across origin and destination countries.",
  },
  {
    q: "Which countries do you serve?",
    a: "Our active network spans 25+ countries across Asia, Africa, Europe and the Americas. Get in touch for route-specific feasibility.",
  },
];

export default function Contact() {
  const ref = useReveal();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState({ state: "idle", error: "" });
  const [openFaq, setOpenFaq] = useState(0);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", error: "" });
    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          "form-name": "contact",
          ...form,
        }).toString(),
      });
      setStatus({ state: "success", error: "" });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      setStatus({
        state: "error",
        error: "Something went wrong. Please try again.",
      });
    }
  };

  return (
    <div ref={ref} data-testid="contact-page">
      {/* HEADER */}
      <section className="relative bg-[#0E2A3F] text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.9) 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="absolute -bottom-32 right-0 h-96 w-96 rounded-full bg-[#F26F21]/15 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-28">
          <span className="reveal text-[11px] uppercase tracking-[0.24em] text-[#F26F21] font-semibold">
            Contact
          </span>
          <h1 className="reveal mt-5 font-[Outfit] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl leading-tight">
            Get in Touch.
          </h1>
          <p className="reveal mt-6 max-w-2xl text-slate-300 text-base lg:text-lg leading-relaxed">
            We&apos;d love to hear from you. Whether you have a question, need a quote, or want
            to discuss your import/export needs, our team is ready to assist.
          </p>
        </div>
      </section>

      {/* CONTACT GRID */}
      <section className="py-24 lg:py-28 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          {/* FORM */}
          <div className="lg:col-span-7 reveal">
            <div className="rounded-2xl bg-white border border-slate-200/70 shadow-sm p-8 lg:p-10">
              <h2 className="font-[Outfit] text-2xl lg:text-3xl font-bold text-[#0E2A3F] tracking-tight">
                Send us a message
              </h2>
              <p className="mt-2 text-sm text-[#475569]">
                Fill in the form and we&apos;ll reply within one business day.
              </p>

              <form
                onSubmit={onSubmit}
                name="contact"
                data-netlify="true"
                className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5"
              >
                <input type="hidden" name="form-name" value="contact" />

                <Field
                  label="Name"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  testId="contact-input-name"
                  required
                />
                <Field
                  label="Email"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  testId="contact-input-email"
                  required
                />
                <Field
                  label="Phone"
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  testId="contact-input-phone"
                  required
                  className="sm:col-span-2"
                />
                <div className="sm:col-span-2">
                  <label className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-[#475569] mb-2">
                    Message
                  </label>
                  <textarea
                    data-testid="contact-input-message"
                    name="message"
                    rows={5}
                    value={form.message}
                    onChange={onChange}
                    required
                    placeholder="Tell us about your shipment, route, volume or any question…"
                    className="w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm text-[#0E2A3F] placeholder:text-slate-400 focus:border-[#F26F21] focus:outline-none focus:ring-2 focus:ring-[#F26F21]/25 transition-colors resize-none"
                  />
                </div>

                {status.state === "success" && (
                  <div
                    data-testid="contact-success"
                    className="sm:col-span-2 flex items-start gap-3 rounded-md bg-green-50 border border-green-200 p-4 text-sm text-green-800"
                  >
                    <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold">Message sent!</div>
                      <div className="text-green-700">We&apos;ve received your inquiry and will respond shortly.</div>
                    </div>
                  </div>
                )}
                {status.state === "error" && (
                  <div
                    data-testid="contact-error"
                    className="sm:col-span-2 flex items-start gap-3 rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-800"
                  >
                    <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold">Something went wrong</div>
                      <div className="text-red-700">{status.error}</div>
                    </div>
                  </div>
                )}

                <div className="sm:col-span-2 flex items-center justify-between gap-4 pt-2">
                  <p className="text-xs text-slate-500">
                    We respect your privacy. Your details are only used to respond to your inquiry.
                  </p>
                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={status.state === "loading"}
                    className="inline-flex items-center gap-2 rounded-md bg-[#F26F21] px-7 py-3.5 text-sm font-semibold text-white shadow-sm hover:bg-[#D95B14] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  >
                    {status.state === "loading" ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        Send Message <Send className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* INFO */}
          <div className="lg:col-span-5 reveal">
            <div className="rounded-2xl bg-[#0E2A3F] text-white p-8 lg:p-10 relative overflow-hidden">
              <div className="absolute -top-20 -right-20 h-60 w-60 rounded-full bg-[#F26F21]/20 blur-3xl" />
              <h3 className="relative font-[Outfit] text-2xl font-bold tracking-tight">Contact Details</h3>
              <p className="relative mt-2 text-sm text-slate-300">
                Reach us through any of the channels below.
              </p>

              <ul className="relative mt-8 space-y-5">
                <InfoRow
                  icon={Mail}
                  label="Email"
                  value={<a href="mailto:mahender@vrvr.in" className="hover:text-[#F26F21]">mahender@vrvr.in</a>}
                  testId="info-email"
                />
                <InfoRow
                  icon={Phone}
                  label="Phone (South Africa)"
                  value={<a href="tel:+27747857141" className="hover:text-[#F26F21]">+27 747 857 141</a>}
                  testId="info-phone-za"
                />
                <InfoRow
                  icon={Phone}
                  label="Phone (India)"
                  value={<a href="tel:+918977109231" className="hover:text-[#F26F21]">+91 8977 109 231</a>}
                  testId="info-phone-in"
                />
                <InfoRow
                  icon={MapPin}
                  label="Address"
                  value={<>4-2-608, Ramkoti, Sultan Bazar,<br />Hyderabad, Telangana, India</>}
                  testId="info-address"
                />
                <InfoRow
                  icon={Clock}
                  label="Business Hours"
                  value={<>Monday – Friday<br />9:00 AM – 6:00 PM IST</>}
                  testId="info-hours"
                />
              </ul>
            </div>

            <div className="mt-6 rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
              <iframe
                title="VRVR Hyderabad Office"
                data-testid="contact-map"
                src="https://www.google.com/maps?q=Ramkoti+Sultan+Bazar+Hyderabad+Telangana+India&output=embed"
                width="100%"
                height="320"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 lg:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center reveal">
            <span className="text-[11px] uppercase tracking-[0.24em] text-[#F26F21] font-semibold">
              FAQ
            </span>
            <h2 className="mt-4 font-[Outfit] text-3xl lg:text-4xl font-bold text-[#0E2A3F] tracking-tight">
              Frequently Asked Questions
            </h2>
            <p className="mt-3 text-[#475569]">
              Quick answers to the most common questions we receive.
            </p>
          </div>

          <div className="mt-12 space-y-3">
            {faqs.map((f, i) => {
              const open = openFaq === i;
              return (
                <div
                  key={f.q}
                  data-testid={`faq-item-${i}`}
                  className={`rounded-xl border transition-colors ${
                    open ? "bg-[#F8FAFC] border-[#F26F21]/60" : "bg-white border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    aria-expanded={open}
                    data-testid={`faq-toggle-${i}`}
                    className="w-full text-left flex items-center justify-between gap-4 p-5 lg:p-6 cursor-pointer"
                  >
                    <span className="font-[Outfit] font-semibold text-[#0E2A3F]">{f.q}</span>
                    <span
                      className={`h-8 w-8 shrink-0 rounded-full flex items-center justify-center text-[#F26F21] border border-[#F26F21]/40 transition-transform ${
                        open ? "rotate-45 bg-[#F26F21] text-white border-[#F26F21]" : ""
                      }`}
                    >
                      +
                    </span>
                  </button>
                  {open && (
                    <div
                      data-testid={`faq-answer-${i}`}
                      className="px-5 lg:px-6 pb-6 text-sm text-[#475569] leading-relaxed"
                    >
                      {f.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", testId, required, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-[#475569] mb-2">
        {label}
      </label>
      <input
        data-testid={testId}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm text-[#0E2A3F] placeholder:text-slate-400 focus:border-[#F26F21] focus:outline-none focus:ring-2 focus:ring-[#F26F21]/25 transition-colors"
      />
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, testId }) {
  return (
    <li className="flex items-start gap-4" data-testid={testId}>
      <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white/10 text-[#F26F21] ring-1 ring-white/15">
        <Icon className="h-4.5 w-4.5" strokeWidth={1.8} />
      </span>
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">{label}</div>
        <div className="mt-1 text-sm text-white leading-relaxed">{value}</div>
      </div>
    </li>
  );
}
